import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import EventDetail from "@/pages/EventDetail";
import MyTickets from "@/pages/MyTickets";
import LoadingScreen from "@/components/LoadingScreen";
import Login from "@/pages/Login";
import CreatorDashboard from "@/pages/CreatorDashboard";
import CreatorSettings from "@/pages/CreatorSettings";
import CreatorAnalytics from "@/pages/CreatorAnalytics";
import CreatorReligiousReport from "@/pages/CreatorReligiousReport";
import CreateEvent from "@/pages/CreateEvent";
import EditEvent from "@/pages/EditEvent";
import StreamDevice from "@/pages/StreamDevice";
import ControlPanel from "@/pages/ControlPanel";
import ProModeControlPanel from "@/pages/ProModeControlPanel";
import ProModeQRCodes from "@/pages/ProModeQRCodes";
import ProModeCameraDevice from "@/pages/ProModeCameraDevice";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import About from "@/pages/About";
import Terms from "@/pages/Terms";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CopyrightDmca from "@/pages/CopyrightDmca";
import AccountDeletion from "@/pages/AccountDeletion";
import CognitoCallback from "@/pages/CognitoCallback";
import {
  fetchCurrentUserWithRetry,
  getAuthRecoveryDelayMs,
  isTransientAuthError,
  shouldClearSessionAfterAuthError,
} from "@/utils/authBootstrap";


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// All API calls go to the backend origin. In local dev REACT_APP_BACKEND_URL
// is empty so this resolves to a same-origin "/api". In production set
// REACT_APP_BACKEND_URL to the backend's public URL.
export const axiosInstance = axios.create({
  baseURL: `${BACKEND_URL || ""}/api`,
  withCredentials: true,
});

// In-memory token storage (works even when cookies + localStorage both fail)
let memoryToken = null;

// Attach token as Bearer header on every request
axiosInstance.interceptors.request.use((config) => {
  const token = memoryToken || (() => { try { return localStorage.getItem("session_token"); } catch(e) { return null; } })();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper to store token in all available storages
const storeToken = (token) => {
  memoryToken = token;
  try { localStorage.setItem("session_token", token); } catch(e) {}
};
const clearToken = () => {
  memoryToken = null;
  try { localStorage.removeItem("session_token"); } catch(e) {}
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAuth, setProcessingAuth] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Restore token from localStorage into memory on mount
    try { memoryToken = localStorage.getItem("session_token"); } catch(e) {}

    // Read a value from BOTH the hash fragment and the query string
    // (some mobile browsers strip the hash during redirect chains)
    const readAuthParam = (key) => {
      let val = null;
      const hash = window.location.hash;
      if (hash) {
        val = new URLSearchParams(hash.substring(1)).get(key);
      }
      if (!val) {
        val = new URLSearchParams(window.location.search).get(key);
      }
      return val;
    };

    const recoverAuthenticatedUser = async () => {
      let recoveryAttempt = 0;
      while (!cancelled) {
        try {
          const response = await fetchCurrentUserWithRetry(axiosInstance);
          if (!cancelled) setUser(response.data);
          return;
        } catch (error) {
          if (!isTransientAuthError(error)) throw error;
          // Keep the authenticated shell loading during a temporary API/DB
          // slowdown. Rendering the login page here made healthy sessions look
          // forcibly signed out during small viewer spikes.
          await new Promise((resolve) => window.setTimeout(
            resolve,
            getAuthRecoveryDelayMs(recoveryAttempt),
          ));
          recoveryAttempt += 1;
        }
      }
    };

    const processAuth = async () => {
      // Our own social-login backend redirects back with a session_token
      // directly (also set as an httpOnly cookie). Pick it up here.
      const sessionToken = readAuthParam("session_token");

      if (sessionToken) {
        setProcessingAuth(true);
        try {
          storeToken(sessionToken);
          await recoverAuthenticatedUser();
        } catch (error) {
          console.error("Auth error:", error);
          // A timeout or temporary 5xx must not erase the valid token that the
          // OAuth callback just issued. Keep it so the retry or next reload can
          // finish signing the user in. Clear only an explicitly rejected token.
          if (shouldClearSessionAfterAuthError(error)) clearToken();
        } finally {
          // Never leave a session token in the visible URL, even when the
          // backend is temporarily unavailable.
          window.history.replaceState(null, "", window.location.pathname);
          setProcessingAuth(false);
        }
      } else {
        try {
          await recoverAuthenticatedUser();
        } catch (error) {
          // Not authenticated
        }
      }

      if (!cancelled) setLoading(false);
    };

    processAuth();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const submitCrash = (message, stack) => {
      axios.post(`${BACKEND_URL || ""}/api/monitoring/client-error`, {
        message: String(message || "Unknown browser error").slice(0, 2000),
        stack: String(stack || "").slice(0, 12000),
        route: `${window.location.pathname}${window.location.search}`.slice(0, 500),
        release: process.env.REACT_APP_RELEASE || "unknown",
      }).catch(() => {});
    };
    const onError = (event) => submitCrash(event.message, event.error?.stack);
    const onUnhandledRejection = (event) => {
      const reason = event.reason;
      submitCrash(reason?.message || reason, reason?.stack);
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    clearToken();
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  if (loading || processingAuth) {
    return <LoadingScreen />;
  }

  // Every authenticated (non-admin) user is BOTH a viewer and a creator.
  // No role picker — creator tools are always one click away in the navbar.
  const isAuthed = !!user;

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* The root URL is sign-in; /events is the public homepage. */}
          <Route path="/" element={
            user ? <Navigate to="/events" replace /> : <Login />
          } />
          <Route path="/events" element={<Home user={user} onLogout={handleLogout} />} />
          <Route path="/browse" element={<Browse user={user} onLogout={handleLogout} />} />
          <Route path="/event/:id" element={<EventDetail user={user} onLogout={handleLogout} />} />
          {/* Hidden legal pages: intentionally omitted from the primary navigation. */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
          <Route path="/copyright-dmca" element={<CopyrightDmca />} />
          <Route path="/account-deletion" element={<AccountDeletion user={user} />} />
          <Route path="/auth/cognito/callback" element={<CognitoCallback />} />
          <Route path="/settings/account" element={
            isAuthed ? <AccountDeletion user={user} /> : <Navigate to="/account-deletion" replace />
          } />

          {/* Single unified Google sign-in, with legacy route compatibility. */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          {/* Back-compat: old role-selection links resolve to the correct landing page. */}
          <Route path="/select-role" element={
            <Navigate to={user ? "/events" : "/"} replace />
          } />

          {/* Viewer routes */}
          <Route path="/my-tickets" element={
            isAuthed ? <MyTickets user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />

          {/* Creator routes — open to any signed-in user (viewer + creator in one account) */}
          <Route path="/creator/dashboard" element={
            isAuthed ? <CreatorDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />
          <Route path="/creator/create-event" element={
            isAuthed ? <CreateEvent user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />
          <Route path="/creator/edit-event/:eventId" element={
            isAuthed ? <EditEvent user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />
          <Route path="/creator/settings" element={
            isAuthed ? <CreatorSettings user={user} onLogout={handleLogout} onUserUpdate={updateUser} /> : <Navigate to="/" replace />
          } />
          <Route path="/creator/analytics" element={
            isAuthed ? <CreatorAnalytics user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />
          <Route path="/creator/religious-report/:eventId" element={
            isAuthed ? <CreatorReligiousReport user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />
          <Route path="/creator/religious-report" element={
            isAuthed ? <CreatorReligiousReport user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />

          {/* Streaming routes */}
          <Route path="/stream/:deviceToken" element={<StreamDevice />} />
          <Route path="/control-panel/:eventId" element={
            isAuthed ? <ControlPanel user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />
          <Route path="/pro-mode/:eventId" element={
            isAuthed ? <ProModeControlPanel user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />
          <Route path="/pro-mode/:eventId/qr" element={
            isAuthed ? <ProModeQRCodes user={user} onLogout={handleLogout} /> : <Navigate to="/" replace />
          } />
          <Route path="/pro-mode/camera/:eventId/:deviceNumber" element={
            <ProModeCameraDevice user={user} />
          } />

          {/* Payment routes */}
          <Route path="/payment-success" element={<PaymentSuccess user={user} onLogout={handleLogout} />} />
          <Route path="/payment-cancel" element={<PaymentCancel user={user} onLogout={handleLogout} />} />

          {/* About page */}
          <Route path="/about" element={<About user={user} onLogout={handleLogout} />} />

          {/* Admin routes */}
          <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
